import { Camera } from "lucide-react";
import { ModalCard } from "@/components/flowly/infra/ModalCard/ModalCard";
import { Button } from "@/components/ui/button";

interface UploadGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Reference photo shown inside the camera-viewfinder frame. */
  imageSrc: string;
  title: string;
  body: string;
  /** Chip + alt text on the reference image (e.g. "shoot it like this"). */
  imageCaption: string;
  ctaLabel: string;
}

/**
 * Centered "how to shoot it" popup shown once after a user's first upload.
 * Renders a reference photo inside a camera-viewfinder frame so the intended
 * framing is obvious. All copy + the reference image come in as props so each
 * flow (profile photo, talking avatar, …) supplies its own text without
 * cloning the layout.
 */
export function UploadGuideModal({
  isOpen,
  onClose,
  imageSrc,
  title,
  body,
  imageCaption,
  ctaLabel,
}: UploadGuideModalProps) {
  return (
    <ModalCard isOpen={isOpen} onClose={onClose} maxWidth="max-w-md">
      <div className="flex flex-col items-center text-center gap-5">
        <div className="flex items-center gap-2 text-brand">
          <Camera className="w-6 h-6" />
          <h2 className="text-lg md:text-xl font-bold text-foreground">{title}</h2>
        </div>

        {/* Reference photo inside a camera-viewfinder frame */}
        <div className="relative w-48 h-60 md:w-56 md:h-72">
          <img
            src={imageSrc}
            alt={imageCaption}
            className="w-full h-full object-cover rounded-2xl"
          />
          {/* Viewfinder corner brackets */}
          <span className="pointer-events-none absolute top-2 start-2 w-8 h-8 border-t-4 border-s-4 border-white rounded-tl-xl" />
          <span className="pointer-events-none absolute top-2 end-2 w-8 h-8 border-t-4 border-e-4 border-white rounded-tr-xl" />
          <span className="pointer-events-none absolute bottom-2 start-2 w-8 h-8 border-b-4 border-s-4 border-white rounded-bl-xl" />
          <span className="pointer-events-none absolute bottom-2 end-2 w-8 h-8 border-b-4 border-e-4 border-white rounded-br-xl" />
          {/* Caption chip */}
          <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand px-3 py-1 text-xs font-semibold text-white shadow-lg">
            {imageCaption}
          </span>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed pt-2">{body}</p>

        <Button onClick={onClose} size="lg" className="w-full">
          {ctaLabel}
        </Button>
      </div>
    </ModalCard>
  );
}
